import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Empty, Table } from 'antd';
import type { MenuProps, TablePaginationConfig } from 'antd';
import { FileTextOutlined, MoreOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import InfiniteScroller from 'components/molecules/InfiniteScroller';

import { useTpoGenConfig } from '../context/CustomerConfigContext';
import {
    TPOGEN_ANALYTICS_VIEWED,
    TPOGEN_COMPARE_PLANS_REPORT_DELETED,
    TPOGEN_COMPARE_PLANS_REPORT_OPENED,
} from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../utils/trackTpoGenEvent';
import {
    TPO_COMPARISON_REPORTS_EMPTY,
    TPO_COMPARISON_REPORTS_LOADING,
    TPO_COMPARISON_REPORTS_SCROLLER,
    TPO_COMPARISON_REPORTS_TABLE,
    tpoPartTestId,
    tpoTableCellTestId,
    tpoTableColTestId,
    tpoTableRowTestId,
} from '../config/testIds';
import useComparisonReportList from './hooks/queries/useComparisonReportList';
import useDeleteComparisonReportMutation from './hooks/mutations/useDeleteComparisonReportMutation';
import useDuplicateComparisonReportMutation from './hooks/mutations/useDuplicateComparisonReportMutation';
import DeleteReportModal from './ScenarioComparison/components/DeleteReportModal';
import CELL_RENDERERS from './cellRenderers';
import type { ComparisonReportListItem } from './types';
import type { DimensionFilter } from '../types/filters';

// Matches the plans table: viewport minus the app chrome, breadcrumb and toolbar above the grid.
const SCROLL_VIEWPORT_OFFSET_PX = 370;

const TABLE_SURFACE = 'comparison_reports';
const NAME_COLUMN_KEY = 'name';
const NAME_LINK_PART = 'name_link';
const ACTIONS_COLUMN_KEY = 'actions';
const ACTIONS_BTN_PART = 'actions_btn';


const DEFAULT_SORT_BY = 'updated_at';
const DEFAULT_SORT_ORDER = 'desc';

const SORT_DIRECTIONS: ColumnType<ComparisonReportListItem>['sortDirections'] = ['descend', 'ascend'];

const getStickyContainer = () =>
    document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement;

interface ComparisonReportsTableProps {
    searchKeyword: string;
    showMyReports: boolean;
    selectedColumns: string[];
    filters: Record<string, DimensionFilter>;
}

const toAntdSortOrder = (isSortedColumn: boolean, sortOrder: 'asc' | 'desc') => {
    if (!isSortedColumn) return null;
    return sortOrder === 'asc' ? ('ascend' as const) : ('descend' as const);
};

const ComparisonReportsTable: FC<ComparisonReportsTableProps> = ({
    searchKeyword,
    showMyReports,
    selectedColumns,
    filters,
}) => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const tpoGenConfig = useTpoGenConfig();
    const { comparisonReportColumns } = tpoGenConfig.scenarioComparisonConfig;
    const { labels } = tpoGenConfig;

    const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_BY);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(DEFAULT_SORT_ORDER);
    const [reportPendingDelete, setReportPendingDelete] = useState<ComparisonReportListItem | null>(null);

    const {
        data: reports,
        isPending,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useComparisonReportList({
        searchKey: searchKeyword,
        myReports: showMyReports,
        filters,
        sortBy,
        sortOrder,
    });

    const { mutate: duplicateReport } = useDuplicateComparisonReportMutation({
        successMessage: labels.comparisonReport.notifications.duplicateSuccess,
        errorMessage: labels.comparisonReport.notifications.duplicateError,
    });
    const { mutate: deleteReport, isPending: isDeleting } = useDeleteComparisonReportMutation({
        successMessage: labels.comparisonReport.notifications.deleteSuccess,
        errorMessage: labels.comparisonReport.notifications.deleteError,
    });

    const hasTrackedAnalyticsViewRef = useRef(false);
    useEffect(() => {
        if (isPending || hasTrackedAnalyticsViewRef.current) return;
        hasTrackedAnalyticsViewRef.current = true;
        trackTpoGenEvent(TPOGEN_ANALYTICS_VIEWED, TPOGEN_PAGE_NAMES.PLANS_LIST, {
            plan_id: null,
            plan_count: 0,
        });
    }, [isPending]);

    /** Column key -> the `sort_by` value the search endpoint expects. */
    const sortFieldByColumnKey = useMemo(() => {
        const sortFields = new Map<string, string>([[NAME_COLUMN_KEY, NAME_COLUMN_KEY]]);
        for (const col of comparisonReportColumns) {
            sortFields.set(col.key, col.sortKey ?? col.dataIndex);
        }
        return sortFields;
    }, [comparisonReportColumns]);

    const handleReportClick = useCallback(
        (record: ComparisonReportListItem) => {
            trackTpoGenEvent(TPOGEN_COMPARE_PLANS_REPORT_OPENED, TPOGEN_PAGE_NAMES.PLANS_LIST, {
                report_id: record.id,
                report_name: record.name,
                change_type: 'open',
            });
            navigate(`/tpo-gen/scenario-comparison/${record.id}`, { state: { fromSearch: search } });
        },
        [navigate, search],
    );

    const handleDuplicate = useCallback(
        (reportId: string) => duplicateReport({ reportId }),
        [duplicateReport],
    );

    const handleDeleteReport = useCallback((record: ComparisonReportListItem) => {
        setReportPendingDelete(record);
    }, []);

    const handleCancelDelete = useCallback(() => setReportPendingDelete(null), []);

    const handleConfirmDelete = useCallback(() => {
        if (!reportPendingDelete) return;
        const { id, name } = reportPendingDelete;
        deleteReport(id, {
            onSuccess: () => {
                trackTpoGenEvent(TPOGEN_COMPARE_PLANS_REPORT_DELETED, TPOGEN_PAGE_NAMES.PLANS_LIST, {
                    report_id: id,
                    report_name: name,
                    change_type: 'delete',
                });
                setReportPendingDelete(null);
            },
        });
    }, [deleteReport, reportPendingDelete]);

    const handleTableChange = useCallback(
        (
            _pagination: TablePaginationConfig,
            _tableFilters: Record<string, FilterValue | null>,
            sorter: SorterResult<ComparisonReportListItem> | SorterResult<ComparisonReportListItem>[],
        ) => {
            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            if (!singleSorter?.order) {
                setSortBy(DEFAULT_SORT_BY);
                setSortOrder(DEFAULT_SORT_ORDER);
                return;
            }
            setSortBy(sortFieldByColumnKey.get(String(singleSorter.columnKey)) ?? DEFAULT_SORT_BY);
            setSortOrder(singleSorter.order === 'ascend' ? 'asc' : 'desc');
        },
        [sortFieldByColumnKey],
    );

    const getRowMenuItems = useCallback(
        (record: ComparisonReportListItem): MenuProps['items'] => {
            const actionsBtnId = tpoTableCellTestId(TABLE_SURFACE, record.id, ACTIONS_BTN_PART);
            return [
                {
                    key: 'duplicate',
                    label: labels.comparisonReport.actions.duplicate,
                    onClick: () => handleDuplicate(record.id),
                    'data-testid': tpoPartTestId(actionsBtnId, 'duplicate'),
                } as NonNullable<MenuProps['items']>[number],
                { type: 'divider' },
                {
                    key: 'delete_report',
                    label: (
                        <span className="text-[#FF4D4F]">
                            {labels.comparisonReport.actions.deleteReport}
                        </span>
                    ),
                    onClick: () => handleDeleteReport(record),
                    'data-testid': tpoPartTestId(actionsBtnId, 'delete_report'),
                } as NonNullable<MenuProps['items']>[number],
            ];
        },
        [labels, handleDuplicate, handleDeleteReport],
    );

    const columns: ColumnsType<ComparisonReportListItem> = useMemo(() => {
        const nameColumn: ColumnsType<ComparisonReportListItem>[number] = {
            title: labels.comparisonReport.table.scenarioComparisonLibrary,
            dataIndex: NAME_COLUMN_KEY,
            key: NAME_COLUMN_KEY,
            sorter: true,
            sortDirections: SORT_DIRECTIONS,
            sortOrder: toAntdSortOrder(sortBy === NAME_COLUMN_KEY, sortOrder),
            onHeaderCell: () =>
                ({
                    'data-testid': tpoTableColTestId(TABLE_SURFACE, NAME_COLUMN_KEY),
                } as React.ThHTMLAttributes<HTMLTableCellElement>),
            render: (text: string, record: ComparisonReportListItem) => (
                <div
                    data-testid={tpoTableCellTestId(TABLE_SURFACE, record.id, NAME_LINK_PART)}
                    className="flex items-center gap-2 cursor-pointer hover:underline"
                    onClick={() => handleReportClick(record)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleReportClick(record);
                    }}
                >
                    <FileTextOutlined className="text-[#807E86]" />
                    <span>{text}</span>
                </div>
            ),
        };

        const configColumns: ColumnsType<ComparisonReportListItem> = comparisonReportColumns
            .filter((col) => selectedColumns.includes(col.key))
            .map((col) => ({
                title: col.label,
                dataIndex: col.dataIndex.includes('.') ? col.dataIndex.split('.') : col.dataIndex,
                key: col.key,
                align: col.align,
                sorter: col.sortable ? true : undefined,
                sortDirections: SORT_DIRECTIONS,
                sortOrder: col.sortable
                    ? toAntdSortOrder(sortBy === sortFieldByColumnKey.get(col.key), sortOrder)
                    : undefined,
                onHeaderCell: () =>
                    ({
                        'data-testid': tpoTableColTestId(TABLE_SURFACE, col.key),
                    } as React.ThHTMLAttributes<HTMLTableCellElement>),
                onCell: (record: ComparisonReportListItem) =>
                    ({
                        'data-testid': tpoTableCellTestId(TABLE_SURFACE, record.id, col.key),
                    } as React.TdHTMLAttributes<HTMLTableCellElement>),
                render: CELL_RENDERERS[col.type],
            }));

        const actionsColumn: ColumnsType<ComparisonReportListItem>[number] = {
            key: ACTIONS_COLUMN_KEY,
            width: 48,
            align: 'center',
            onHeaderCell: () =>
                ({
                    'data-testid': tpoTableColTestId(TABLE_SURFACE, ACTIONS_COLUMN_KEY),
                } as React.ThHTMLAttributes<HTMLTableCellElement>),
            render: (_: unknown, record: ComparisonReportListItem) => (
                <Dropdown
                    menu={{ items: getRowMenuItems(record) }}
                    trigger={['click']}
                    overlayStyle={{ width: '10.9375rem', borderRadius: '0.5rem' }}
                >
                    <MoreOutlined
                        data-testid={tpoTableCellTestId(TABLE_SURFACE, record.id, ACTIONS_BTN_PART)}
                        style={{ fontSize: 16, cursor: 'pointer' }}
                    />
                </Dropdown>
            ),
        };

        return [nameColumn, ...configColumns, actionsColumn];
    }, [
        comparisonReportColumns,
        labels,
        handleReportClick,
        selectedColumns,
        getRowMenuItems,
        sortBy,
        sortOrder,
        sortFieldByColumnKey,
    ]);

    const isTableLoading = isPending || (isFetching && !isFetchingNextPage);

    // Loading and empty are distinct states for QA: AntD renders its own spin
    // overlay while `loading` is true, so the empty marker must not appear then.
    const tableLocale = useMemo(
        () => ({
            emptyText: isTableLoading ? (
                <div data-testid={TPO_COMPARISON_REPORTS_LOADING} />
            ) : (
                <div data-testid={TPO_COMPARISON_REPORTS_EMPTY}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={labels.common.noData} />
                </div>
            ),
        }),
        [isTableLoading, labels],
    );

    return (
        <>
            <InfiniteScroller
                data={reports}
                loading={isFetchingNextPage}
                hasMore={hasNextPage}
                height={window.innerHeight - SCROLL_VIEWPORT_OFFSET_PX}
                onNext={() => fetchNextPage()}
                data-testid={TPO_COMPARISON_REPORTS_SCROLLER}
            >
                <Table<ComparisonReportListItem>
                    columns={columns}
                    dataSource={reports}
                    rowKey="id"
                    pagination={false}
                    loading={isTableLoading}
                    locale={tableLocale}
                    onChange={handleTableChange}
                    data-testid={TPO_COMPARISON_REPORTS_TABLE}
                    onRow={(record) =>
                        ({
                            'data-testid': tpoTableRowTestId(TABLE_SURFACE, record.id),
                        } as React.HTMLAttributes<HTMLTableRowElement>)
                    }
                    sticky={{ getContainer: getStickyContainer }}
                    scroll={{ x: '100%' }}
                />
            </InfiniteScroller>

            <DeleteReportModal
                open={!!reportPendingDelete}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                reportName={reportPendingDelete?.name ?? ''}
                isPending={isDeleting}
            />
        </>
    );
};

ComparisonReportsTable.displayName = 'ComparisonReportsTable';
export default ComparisonReportsTable;
